import * as React from 'react';

import moment, { Moment } from 'moment';

import MuiCheckbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MuiRadio from '@material-ui/core/Radio';
import MuiRadioGroup from '@material-ui/core/RadioGroup';
import MuiSelect from '@material-ui/core/Select';
import MuiSlider from '@material-ui/core/Slider';
import MuiTextField from '@material-ui/core/TextField';

import { UiDatePicker } from '../../ui/ui-date-picker';

export const TextField = ({
  input,
  meta: { touched, invalid, error },
  variant,
  ...custom
}: ReduxFormFieldProps<string>) => (
  <MuiTextField
    variant={variant}
    error={touched && invalid}
    helperText={touched && error}
    {...input}
    {...custom}
  />
);

export const Checkbox = ({ input, label }: ReduxFormFieldProps<boolean>) => (
  <div>
    <FormControlLabel
      control={<MuiCheckbox checked={Boolean(input.value)} onChange={input.onChange} />}
      label={label}
    />
  </div>
);

export const RadioGroup = ({ input, children, ...rest }: ReduxFormFieldProps<string>) => (
  <FormControl>
    <MuiRadioGroup {...input} {...rest}>
      {children}
    </MuiRadioGroup>
  </FormControl>
);

export const RadioButton = ({ label, value }: { label: string; value: string }) => (
  <FormControlLabel value={value} control={<MuiRadio />} label={label} />
);

export const FormHelperText = ({
  touched,
  error,
}: {
  touched: boolean;
  error: string | undefined | null;
}) => {
  if (!(touched && error)) {
    return null;
  }
  return <MuiFormHelperText>{touched && error}</MuiFormHelperText>;
};

export const Select = ({
  input,
  label,
  meta: { touched, error },
  children,
  id,
  name,
  ...custom
}: ReduxFormFieldProps<React.ReactNode>) => (
  <FormControl error={Boolean(touched && error)}>
    <InputLabel htmlFor={id ?? ''}>{label}</InputLabel>
    <MuiSelect
      {...input}
      {...custom}
      inputProps={{
        name,
        id,
      }}
    >
      {children}
    </MuiSelect>
    <FormHelperText touched={touched} error={error} />
  </FormControl>
);

const sliderOnChange =
  (onChange: Function, twoWayTransform?: Record<string, any>) =>
  (event: Record<string, any>, value: any) => {
    onChange(twoWayTransform ? twoWayTransform[value] : value);
  };

type SliderProps = ReduxFormFieldProps<number> & { twoWayTransform?: Record<string, any> };
export const Slider = ({
  input,
  label,
  meta: { touched, error },
  id,
  name,
  twoWayTransform,
  ...custom
}: SliderProps) => (
  <FormControl error={Boolean(touched && error)} style={{ width: '80%', marginLeft: '10%' }}>
    <InputLabel htmlFor={id ?? ''}>{label}</InputLabel>
    <MuiSlider
      {...input}
      {...custom}
      value={twoWayTransform ? twoWayTransform[Number(input.value)] : Number(input.value)}
      onChange={sliderOnChange(input.onChange, twoWayTransform)}
    />
    <FormHelperText touched={touched} error={error} />
  </FormControl>
);

export const DateField = (props: ReduxFormFieldProps<string>) => {
  const {
    input: { value, name, onChange, onBlur, onFocus },
    variant,
    ...datePickerProps
  } = props;

  const handleChange = (date: Moment | null) => {
    if (!date) {
      onChange('');
    } else if (date.isValid()) {
      onChange(date.toISOString());
    }
  };

  return (
    <UiDatePicker
      {...datePickerProps}
      name={name}
      onBlur={onBlur}
      onFocus={onFocus}
      onChange={handleChange}
      defaultDate={value ? moment.utc(value) : ''}
    />
  );
};
